import React, { useState, useEffect } from 'react';
import { getAllProjectsWithScores, submitFeedback } from '../utils/judge';
import { supabase } from '../lib/supabase';
import JudgeLayout from '../layouts/JudgeLayout';
import ProjectCard from './ProjectCard';
import { FiX, FiCheckCircle, FiXCircle } from 'react-icons/fi';

const JudgeProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, qualified, pending, disqualified

  // Judge Identity
  const [judgeName, setJudgeName] = useState(localStorage.getItem('judge_name') || '');
  const [showNameModal, setShowNameModal] = useState(!localStorage.getItem('judge_name'));
  const [tempJudgeName, setTempJudgeName] = useState('');

  // Project Selection
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Modals
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showScreeningModal, setShowScreeningModal] = useState(false);
  const [showFeedbackHistory, setShowFeedbackHistory] = useState(false);

  // Forms
  const [screeningForm, setScreeningForm] = useState({
    is_theme_matched: false
  });

  const [feedbackForm, setFeedbackForm] = useState({
    feedback: '',
    // Ratings (0-10)
    daily_value_score: 0,
    productivity_score: 0,
    mvp_quality_score: 0,
    arabic_focus_score: 0,
    innovation_score: 0,
    scalability_score: 0,
    
    // Questions
    is_theme_matched: true,
    is_ai_used: false,
    are_commits_consistent: false
  });

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchProjects();

    // Subscribe to realtime changes
    const channel = supabase
      .channel('judge_updates')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'project_feedback' },
        (payload) => {
          console.log('Realtime feedback update:', payload);
          fetchProjects(true);
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'projects' },
        (payload) => {
          console.log('Realtime project update:', payload);
          fetchProjects(true);
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchProjects = async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      const result = await getAllProjectsWithScores();
      if (result.success) {
        setProjects(result.data);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('حدث خطأ في جلب المشاريع');
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (tempJudgeName.trim()) {
      localStorage.setItem('judge_name', tempJudgeName.trim());
      setJudgeName(tempJudgeName.trim());
      setShowNameModal(false);
    }
  };

  const handleScreeningSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');

    try {
      const result = await submitFeedback({
        project_id: selectedProject.id,
        judge_name: judgeName,
        is_theme_matched: screeningForm.is_theme_matched,
        score: null, // Screening only
        feedback: screeningForm.is_theme_matched ? 'تمت المطابقة مع الثيم' : 'غير مطابق للثيم', // Auto comment
        user_agent: navigator.userAgent
      });

      if (result.success) {
        setMessage('تم حفظ التصفية بنجاح');
        await fetchProjects(true); // Background update
        setTimeout(() => {
          setShowScreeningModal(false);
          setMessage('');
          setScreeningForm({ is_theme_matched: false });
        }, 1000);
      } else {
        setMessage('حدث خطأ أثناء الحفظ');
      }
    } catch (err) {
      console.error(err);
      setMessage('حدث خطأ غير متوقع');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');

    if (!selectedProject) return;

    try {
      // Calculate total score based on weights
      // Innovation (40%), Daily Value (20%), Others (10% each)
      // Scores are 0-10
      const totalScore = (
        (parseInt(feedbackForm.innovation_score) * 4) +
        (parseInt(feedbackForm.daily_value_score) * 2) +
        (parseInt(feedbackForm.productivity_score) * 1) +
        (parseInt(feedbackForm.mvp_quality_score) * 1) +
        (parseInt(feedbackForm.arabic_focus_score) * 1) +
        (parseInt(feedbackForm.scalability_score) * 1)
      ); // Max possible: 40+20+10+10+10+10 = 100

      const result = await submitFeedback({
        project_id: selectedProject.id,
        judge_name: judgeName,
        ...feedbackForm,
        score: totalScore,
        user_agent: navigator.userAgent
      });

      if (result.success) {
        setMessage('تم إرسال التقييم بنجاح');
        setFeedbackForm({
          feedback: '',
          daily_value_score: 0,
          productivity_score: 0,
          mvp_quality_score: 0,
          arabic_focus_score: 0,
          innovation_score: 0,
          scalability_score: 0,
          is_theme_matched: true,
          is_ai_used: false,
          are_commits_consistent: false
        });
        await fetchProjects(true); // Background update
        setTimeout(() => {
          setShowFeedbackModal(false);
          setMessage('');
        }, 1500);
      } else {
        setMessage('حدث خطأ أثناء إرسال التقييم');
      }
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setMessage('حدث خطأ أثناء إرسال التقييم');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <JudgeLayout>
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow"></div>
        </div>
      </JudgeLayout>
    );
  }

  return (
    <JudgeLayout>
      {/* Name Modal */}
      {showNameModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl max-w-md w-full p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-6">أهلاً بك في لجنة التحكيم</h2>
            <form onSubmit={handleNameSubmit} className="space-y-4">
              <div>
                <label className="block text-right text-yellow mb-2 text-sm">الاسم الكريم</label>
                <input
                  type="text"
                  required
                  value={tempJudgeName}
                  onChange={(e) => setTempJudgeName(e.target.value)}
                  className="w-full px-4 py-3 bg-blue/20 border border-yellow/50 rounded-xl focus:outline-none focus:border-yellow text-white text-center text-lg"
                  placeholder="اكتب اسمك هنا"
                />
              </div>
              <button
                type="submit"
                className="w-full py-3 bg-yellow text-dark-blue font-bold rounded-xl hover:bg-yellow/90 transition-all"
              >
                دخول
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="mb-8 flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-yellow mb-2">
            تحكيم <span className="text-white">المشاريع</span>
          </h1>
          <p className="text-white/70">مرحباً {judgeName}، يرجى تصفية وتقييم المشاريع.</p>
        </div>
        <button 
          onClick={() => setShowNameModal(true)}
          className="text-sm text-yellow underline hover:text-white transition-colors"
        >
          تغيير الاسم
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
        {[
          { id: 'all', label: 'الكل' },
          { id: 'qualified', label: 'المؤهلة' },
          { id: 'pending', label: 'قيد التصفية' },
          { id: 'disqualified', label: 'المستبعدة' }
        ].map(tab => {
          const count = projects.filter(p => {
            if (tab.id === 'all') return true;
            return p.screeningStatus === tab.id;
          }).length;
          
          return (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
                filter === tab.id 
                  ? 'bg-yellow text-dark-blue' 
                  : 'bg-white/5 text-white/50 hover:bg-white/10'
              }`}
            >
              {tab.label} ({count})
            </button>
          );
        })}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-200 px-6 py-4 rounded-xl mb-6 text-center">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {projects
          .filter(project => {
            if (filter === 'all') return true;
            return project.screeningStatus === filter;
          })
          .sort((a, b) => {
            // Priority: Pending > Qualified > Disqualified
            const statusPriority = { pending: 3, qualified: 2, disqualified: 1 };
            const priorityDiff = statusPriority[b.screeningStatus] - statusPriority[a.screeningStatus];
            if (priorityDiff !== 0) return priorityDiff;
            
            // Secondary sort: Score for qualified (desc), or ID/Name for others
            if (a.screeningStatus === 'qualified' && b.screeningStatus === 'qualified') {
              return (b.averageScore || 0) - (a.averageScore || 0);
            }
            return 0;
          })
          .map((project, index) => (
          <ProjectCard 
            key={project.id}
            project={project}
            judgeName={judgeName}
            onScreening={(p) => {
              setSelectedProject(p);
              setScreeningForm({ is_theme_matched: false });
              setShowScreeningModal(true);
            }}
            onEvaluate={(p) => {
              setSelectedProject(p);
              setShowFeedbackModal(true);
              setShowFeedbackHistory(false);
            }}
            onHistory={(p) => {
              setSelectedProject(p);
              setShowFeedbackModal(true);
              setShowFeedbackHistory(true);
            }}
          />
        ))}
      </div>

      {/* Screening Modal */}
      {showScreeningModal && selectedProject && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl max-w-lg w-full">
            <div className="bg-yellow px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-dark-blue">تصفية: {selectedProject.team?.team_name}</h3>
              <button onClick={() => setShowScreeningModal(false)} className="text-dark-blue hover:bg-white/20 p-2 rounded-lg">
                <FiX size={24} />
              </button>
            </div>
            
            <form onSubmit={handleScreeningSubmit} className="p-8 space-y-8">
              {message && (
                <div className="p-3 bg-blue/20 text-yellow text-center rounded-lg border border-yellow/20">
                  {message}
                </div>
              )}

              <div className="text-center">
                <p className="text-white text-lg mb-6">هل هذا المشروع مطابق لثيم الهاكاثون؟</p>
                <div className="flex justify-center gap-4">
                  <label className={`cursor-pointer px-6 py-4 rounded-xl border-2 transition-all ${!screeningForm.is_theme_matched ? 'bg-red-500/20 border-red-500' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>
                    <input
                      type="radio"
                      name="theme_match"
                      className="hidden"
                      checked={!screeningForm.is_theme_matched}
                      onChange={() => setScreeningForm({ is_theme_matched: false })}
                    />
                    <div className="flex flex-col items-center gap-2">
                      <FiXCircle size={32} className={!screeningForm.is_theme_matched ? 'text-red-500' : 'text-white/30'} />
                      <span className={!screeningForm.is_theme_matched ? 'text-red-400 font-bold' : 'text-white/50'}>غير مطابق</span>
                    </div>
                  </label>

                  <label className={`cursor-pointer px-6 py-4 rounded-xl border-2 transition-all ${screeningForm.is_theme_matched ? 'bg-green-500/20 border-green-500' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>
                    <input
                      type="radio"
                      name="theme_match"
                      className="hidden"
                      checked={screeningForm.is_theme_matched}
                      onChange={() => setScreeningForm({ is_theme_matched: true })}
                    />
                    <div className="flex flex-col items-center gap-2">
                      <FiCheckCircle size={32} className={screeningForm.is_theme_matched ? 'text-green-500' : 'text-white/30'} />
                      <span className={screeningForm.is_theme_matched ? 'text-green-400 font-bold' : 'text-white/50'}>مطابق</span>
                    </div>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 bg-yellow text-dark-blue font-bold rounded-xl hover:bg-yellow/90 transition-all disabled:opacity-50"
              >
                {submitting ? 'جاري الحفظ...' : 'حفظ قراري'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Evaluation Modal (Simplified from original) */}
      {showFeedbackModal && selectedProject && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl max-w-3xl w-full max-h-[95vh] overflow-y-auto">
            <div className="bg-yellow px-6 py-4 flex items-center justify-between sticky top-0 z-10">
              <h3 className="text-xl font-bold text-dark-blue">
                {showFeedbackHistory 
                  ? `التقييمات: ${selectedProject.team?.team_name}`
                  : `تقييم: ${selectedProject.team?.team_name}`
                }
              </h3>
              <button
                onClick={() => setShowFeedbackModal(false)}
                className="text-dark-blue hover:bg-white/20 p-2 rounded-lg"
              >
                <FiX size={24} />
              </button>
            </div>

            {showFeedbackHistory ? (
              <div className="p-6">
                {selectedProject.feedbackList && selectedProject.feedbackList.length > 0 ? (
                  <div className="space-y-4">
                    {selectedProject.feedbackList
                      .filter(feedback => {
                        // If project is qualified, only show final evaluations (score !== null)
                        // If project is not qualified yet (or disqualified), show everything including screening
                        if (selectedProject.screeningStatus === 'qualified') {
                          return feedback.score !== null;
                        }
                        return true;
                      })
                      .map((feedback) => (
                      <div key={feedback.id} className="bg-blue/20 p-4 rounded-xl border border-white/5">
                         <div className="flex justify-between items-start mb-2">
                           <div>
                             <span className="text-yellow font-bold block">{feedback.judge_name}</span>
                             <div className="flex gap-2 mt-1">
                               {feedback.score === null ? (
                                 <span className="text-white/60 text-xs bg-white/10 px-2 py-1 rounded">تصفية أولية: {feedback.is_theme_matched ? 'مطابق' : 'غير مطابق'}</span>
                               ) : (
                                 <>
                                  <span className="text-yellow text-sm bg-blue/40 px-2 py-0.5 rounded">تقييم نهائي</span>
                                  {feedback.is_ai_used && <span className="text-yellow text-xs bg-purple-500/20 px-2 py-0.5 rounded border border-purple-500/30">AI مستخدم</span>}
                                  {feedback.are_commits_consistent && <span className="text-yellow text-xs bg-green-500/20 px-2 py-0.5 rounded border border-green-500/30">Commits مترابطة</span>}
                                 </>
                               )}
                             </div>
                           </div>
                           <div className="flex flex-col items-end">
                             {feedback.score !== null && (
                               <span className="bg-yellow/20 text-yellow px-2 py-1 rounded-lg text-sm font-bold mb-1">
                                 {feedback.score}/100
                               </span>
                             )}
                           </div>
                         </div>
                         <p className="text-white/80 text-sm leading-relaxed mb-3">{feedback.feedback}</p>
                         
                         {feedback.score !== null && (
                           <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-3 border-t border-white/5">
                             {[
                               { label: 'الابتكار (Innovation)', val: feedback.innovation_score },
                               { label: 'القيمة اليومية (Daily Value)', val: feedback.daily_value_score },
                               { label: 'التأثير الإنتاجي (Productivity Impact)', val: feedback.productivity_score },
                               { label: 'جودة التنفيذ (MVP Quality)', val: feedback.mvp_quality_score },
                               { label: 'مناسبة للمبرمج العربي', val: feedback.arabic_focus_score },
                               { label: 'قابلية التوسع (Scalability)', val: feedback.scalability_score },
                             ].map((item, idx) => (
                               <div key={idx} className="flex justify-between items-center bg-white/5 px-2 py-1.5 rounded text-xs">
                                 <span className="text-white/60">{item.label}</span>
                                 <span className="text-yellow font-bold font-mono text-base">{item.val || 0}</span>
                               </div>
                             ))}
                           </div>
                         )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-white/50">
                    <p>لا توجد بيانات سابقة</p>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleFeedbackSubmit} className="p-6 space-y-6">
                {message && (
                  <div className="p-4 rounded-lg text-center bg-blue/20 text-white border border-white/10">
                    {message}
                  </div>
                )}
                
                {/* Additional Questions */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-yellow mb-2">أسئلة إضافية</h4>
                  <label className="flex items-center gap-3 p-3 bg-blue/20 rounded-xl border border-yellow/20 cursor-pointer hover:bg-blue/30 transition-colors">
                    <input
                      type="checkbox"
                      checked={feedbackForm.is_ai_used}
                      onChange={(e) => setFeedbackForm({...feedbackForm, is_ai_used: e.target.checked})}
                      className="w-5 h-5 rounded border-yellow text-yellow focus:ring-yellow bg-transparent"
                    />
                    <span className="text-white">هل تم استعمال الذكاء الاصطناعي في المشروع؟</span>
                  </label>

                  <label className="flex items-center gap-3 p-3 bg-blue/20 rounded-xl border border-yellow/20 cursor-pointer hover:bg-blue/30 transition-colors">
                    <input
                      type="checkbox"
                      checked={feedbackForm.are_commits_consistent}
                      onChange={(e) => setFeedbackForm({...feedbackForm, are_commits_consistent: e.target.checked})}
                      className="w-5 h-5 rounded border-yellow text-yellow focus:ring-yellow bg-transparent"
                    />
                    <span className="text-white">هل الـ commits مترابطة؟</span>
                  </label>
                </div>

                {/* Score Sliders */}
                <div className="space-y-6">
                  <div className="flex justify-between items-center text-yellow border-b border-yellow/20 pb-2">
                    <span className="font-bold">معايير التقييم</span>
                    <span className="text-sm">المجموع: {
                      (parseInt(feedbackForm.innovation_score) * 4) +
                      (parseInt(feedbackForm.daily_value_score) * 2) +
                      (parseInt(feedbackForm.productivity_score) * 1) +
                      (parseInt(feedbackForm.mvp_quality_score) * 1) +
                      (parseInt(feedbackForm.arabic_focus_score) * 1) +
                      (parseInt(feedbackForm.scalability_score) * 1)
                    }%</span>
                  </div>
                  
                  {[
                    { 
                      key: 'innovation_score', 
                      label: 'الابتكار (Innovation)', 
                      weight: '40%',
                      desc: 'هل الفكرة جديدة؟ أو تحل مشكلة قديمة بطريقة مبتكرة؟'
                    },
                    { 
                      key: 'daily_value_score', 
                      label: 'القيمة اليومية (Daily Value)', 
                      weight: '20%',
                      desc: 'هل الأداة تُستخدم يومياً؟ هل تحل مشكلة أساسية؟'
                    },
                    { 
                      key: 'productivity_score', 
                      label: 'التأثير الإنتاجي (Productivity Impact)', 
                      weight: '10%',
                      desc: 'هل توفر وقت؟ تقلل أخطاء؟ تسرع العمل؟'
                    },
                    { 
                      key: 'mvp_quality_score', 
                      label: 'جودة التنفيذ (MVP Quality)', 
                      weight: '10%',
                      desc: 'هل الأداة تعمل فعلياً؟ هل هي سهلة الاستخدام؟'
                    },
                    { 
                      key: 'arabic_focus_score', 
                      label: 'مناسبة للمبرمج العربي', 
                      weight: '10%',
                      desc: 'هل تدعم العربية؟ هل المحتوى مناسب ثقافياً؟'
                    },
                    { 
                      key: 'scalability_score', 
                      label: 'قابلية التوسع (Scalability)', 
                      weight: '10%',
                      desc: 'هل يمكن تطويرها وجذب مستخدمين وبناء نموذج ربحي؟'
                    }
                  ].map((field) => (
                    <div key={field.key} className="bg-white/5 p-4 rounded-xl border border-white/5">
                      <div className="flex justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-bold">{field.label}</span>
                            <span className="text-yellow text-xs bg-yellow/10 px-2 py-0.5 rounded border border-yellow/20">{field.weight}</span>
                          </div>
                          <p className="text-xs text-white/50 mt-1">{field.desc}</p>
                        </div>
                        <span className="text-yellow font-bold text-xl">{feedbackForm[field.key]}</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        step="1"
                        value={feedbackForm[field.key]}
                        onChange={(e) => setFeedbackForm({...feedbackForm, [field.key]: parseInt(e.target.value)})}
                        className="w-full h-2 bg-blue/30 rounded-lg appearance-none cursor-pointer accent-yellow"
                      />
                      <div className="flex justify-between text-[10px] text-white/30 mt-1">
                        <span>0 (سيء)</span>
                        <span>5 (جيد)</span>
                        <span>10 (ممتاز)</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <label className="block text-sm font-medium text-yellow mb-2">ملاحظات إضافية</label>
                  <textarea
                    rows="3"
                    value={feedbackForm.feedback}
                    onChange={(e) => setFeedbackForm({...feedbackForm, feedback: e.target.value})}
                    className="w-full px-4 py-3 bg-blue/20 border border-yellow/50 rounded-xl focus:outline-none text-white"
                  />
                </div>

                <div className="pt-4 border-t border-white/10">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 bg-yellow text-dark-blue font-bold rounded-xl hover:bg-yellow/90 transition-all disabled:opacity-50"
                  >
                    {submitting ? 'جاري الإرسال...' : 'إرسال التقييم النهائي'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </JudgeLayout>
  );
};

export default JudgeProjects;
